<script lang="ts" setup>
import { ref, useAttrs, computed } from 'vue'
import { useRoute } from 'vue-router'

import { languagesList } from '@/constants/languages'
import { resolveRouteName, useForceUpdate, checkTranslation } from '@/utils/utils'

import GlobalIcon from '../components/Icons/GlobalIcon.vue'
import GlobalIconOutlined from '../components/Icons/GlobalIconOutlined.vue'
import PolygonIcon from '../components/Icons/PolygonIcon.vue'
import PolygonIconRed from '../components/Icons/PolygonIconRed.vue'
import PolygonIconWhite from '../components/Icons/PolygonIconWhite.vue'

import GlobalIconRed from './Icons/GlobalIconRed.vue'

const { renderComponent } = useForceUpdate()

const emit = defineEmits(['toggleSwitcher'])
const route = useRoute()

const hideLanguageSwitcher = ref(true)

const toggleLanguageSwitcher = () => {
  hideLanguageSwitcher.value = !hideLanguageSwitcher.value
  emit('toggleSwitcher')
}

const validLanguages = computed(() =>
  languagesList.filter((language) => {
    const params = { lang: language.abbreviation }
    return checkTranslation(route.name, params)
  })
)

const currentLanguage = computed(() => {
  const langParam = route.params.lang as string
  return langParam || 'en'
})

const attrs$ = useAttrs()

defineProps({
  isMegaMenu: Boolean
})
</script>

<template>
  <div v-if="renderComponent" class="relative" v-bind="attrs$">
    <button
      :class="[isMegaMenu ? 'pb-[7px]' : '']"
      class="inline-flex items-center justify-center h-full font-medium text-white cursor-pointer"
      type="button"
      @click="toggleLanguageSwitcher()"
    >
      <GlobalIcon
        v-if="(!hideLanguageSwitcher && !isMegaMenu) || (hideLanguageSwitcher && isMegaMenu)"
        class="mr-1"
      ></GlobalIcon>
      <GlobalIconOutlined
        v-if="hideLanguageSwitcher && !isMegaMenu"
        class="mr-1"
      ></GlobalIconOutlined>
      <GlobalIconRed v-if="!hideLanguageSwitcher && isMegaMenu" class="h-full mr-1"></GlobalIconRed>
      <span
        :class="[
          hideLanguageSwitcher ? '' : 'text-black',
          isMegaMenu ? 'text-black' : '',
          !hideLanguageSwitcher && isMegaMenu ? 'text-hantec-primary' : ''
        ]"
        class="flex items-center h-full mr-1 text-sm font-bold leading-normal capitalize"
      >
        {{ currentLanguage }}
      </span>
      <PolygonIcon
        v-if="(!hideLanguageSwitcher && !isMegaMenu) || (hideLanguageSwitcher && isMegaMenu)"
        class="h-full mt-[3px]"
      ></PolygonIcon>
      <PolygonIconWhite
        v-if="hideLanguageSwitcher && !isMegaMenu"
        class="h-full mt-[3px]"
      ></PolygonIconWhite>
      <PolygonIconRed
        v-if="!hideLanguageSwitcher && isMegaMenu"
        class="h-full mt-[3px]"
      ></PolygonIconRed>
    </button>
    <!-- Dropdown -->
    <div
      :class="{ hidden: hideLanguageSwitcher }"
      class="z-50 text-base list-none bg-white absolute right-0 top-[-230px] 2lg:top-full divide-y divide-gray-100 shadow dark:bg-gray-700 z-[1000]"
    >
      <ul class="py-2 font-medium" role="none">
        <li v-for="language in validLanguages" :key="language.abbreviation">
          <a
            :href="resolveRouteName(route.name, { lang: language.abbreviation })"
            :lang="language.abbreviation"
            class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
            role="menuitem"
          >
            <div class="inline-flex items-center">
              <component
                :is="language.flag"
                :class="{ grayscale: language.abbreviation === currentLanguage }"
                class="mr-2.5"
              ></component>
              <div
                :class="{ 'text-gray-500': language.abbreviation === currentLanguage }"
                class="pr-27 whitespace-nowrap"
              >
                {{ language.language }}
              </div>
            </div>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>
